import {handleApiError} from 'middleware/errorHandler';
import {selectIsLoggedIn} from 'store/authSlice';
import errorLogger from 'utils/errors';

const AUTH_BACKGROUND_ENDPOINTS = new Set(['getMe', 'refreshToken']);

// TableEditor shows a dedicated Alert for dbt schema fetch failures, and
// useTokenRefresh shows the session-expired prompt for refresh failures
// (e.g. a 403 when the OIDC refresh token expired), so these endpoints are
// excluded from the global popup here. Tour and preference endpoints fail
// silently: the only effect is that no tour prompt appears.
const SELF_HANDLED_ENDPOINTS = new Set([
  'fetchDbtSchema',
  'refreshToken',
  'getUserPreferences',
  'updateUserPreferences',
  'getTourViews',
  'saveTourView',
]);

export const rtkQueryErrorMiddleware = (api) => (next) => (action) => {
  if (action.type.endsWith('/rejected')) {
    const {payload, meta} = action;

    const isAbortedRequest =
      meta?.aborted ||
      action?.error?.name === 'AbortError' ||
      action?.error?.code === 'ERR_CANCELED' ||
      payload?.name === 'AbortError';

    // Aborted/canceled requests are expected during redirects/navigation.
    // They should not be reported as API failures.
    if (isAbortedRequest) {
      return next(action);
    }

    // condition: true means RTK Query skipped the request due to deduplication
    // or cache — not a real failure.
    if (meta?.condition === true) {
      return next(action);
    }

    const state = api.getState(); // Get current state safely

    // 1. Log to Datadog with user context from state
    const trackError = errorLogger.logApiError(payload, meta, state);

    // 2. Trigger UI handler
    // Let route-level auth handling own unauthenticated UX for /auth/me/ 401s.
    // This avoids races where middleware can show "Session Expired" while
    // ProtectedRoute shows "Authentication Required" for the same navigation.
    const isGetMe401 =
      trackError.status === 401 && meta?.arg?.endpointName === 'getMe';

    // Skip 401 errors when the user is not logged in — a 401 on the login
    // page means "not yet authenticated", not "session expired".
    const isUnauthenticated401 =
      trackError.status === 401 && !selectIsLoggedIn(state);

    // Logged-in 401s are now owned by baseQueryWithReauth (silent refresh +
    // replay, or the reconnect prompt on failure) — skip the dialog here to
    // avoid it firing before/instead of that flow.
    const isLoggedIn401 = trackError.status === 401 && selectIsLoggedIn(state);

    // Skip network errors from auth endpoints that fire when the user goes
    // offline — getMe polls in the background (resumes when connectivity
    // returns) and refreshToken failures are already handled by useTokenRefresh
    // (it logs the user out). Neither needs UI error handling.
    // status:0 alone is intentionally excluded: it also covers CORS blocks,
    // which are actionable config errors that should reach handleApiError.
    const isConnectivityError =
      trackError.originalStatus === 'FETCH_ERROR' ||
      trackError.name === 'NetworkError';

    const isOfflineAuthPoll =
      isConnectivityError &&
      AUTH_BACKGROUND_ENDPOINTS.has(meta?.arg?.endpointName);

    const isSelfHandled = SELF_HANDLED_ENDPOINTS.has(meta?.arg?.endpointName);

    if (
      trackError.status !== 502 &&
      !isGetMe401 &&
      !isUnauthenticated401 &&
      !isLoggedIn401 &&
      !isOfflineAuthPoll &&
      !isSelfHandled
    ) {
      handleApiError(trackError, {}, api.dispatch, state);
    }
  }
  return next(action);
};
